import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';
import { Item } from '../models/Item';

const CATEGORIES = ['Tire', 'Oil', 'Rim', 'Other'];

const LowStockDialog = ({ open, onClose, shopName, onUpdate }) => {
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState(null);

  const loadLowStockItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await inventoryService.getAllItems(shopName);
      const lowStock = items.filter(item => {
        const qty = Number(item.quantity) || 0;
        return qty > 0 && qty <= 5;
      });
      setLowStockItems(lowStock);
    } catch (error) {
      setError('Failed to load low stock items');
      console.error('Error loading low stock items:', error);
    } finally {
      setLoading(false);
    }
  }, [shopName]);

  useEffect(() => {
    if (open) {
      loadLowStockItems();
    }
  }, [open, loadLowStockItems]);

  const startEditing = (rowIndex, field, value) => {
    setEditingCell({ rowIndex, field });
    setEditValue(value || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEditing = async () => {
    if (!editingCell) return;

    try {
      const { rowIndex, field } = editingCell;
      const item = lowStockItems[rowIndex];
      const updatedItem = new Item({ ...item });

      // Update the field
      if (field === 'quantity' || field === 'sellingPrice' || field === 'originalCost') {
        updatedItem[field] = parseFloat(editValue) || 0;
      } else {
        updatedItem[field] = editValue;
      }

      await inventoryService.updateItem(updatedItem);

      // Update local state
      const updatedItems = [...lowStockItems];
      updatedItems[rowIndex] = updatedItem;
      setLowStockItems(updatedItems);

      if (onUpdate) {
        await onUpdate();
      }

      cancelEditing();
    } catch (error) {
      setError('Error updating item: ' + error.message);
      console.error('Error updating item:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEditing();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const renderCell = (item, field, value, rowIndex) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

    if (isEditing) {
      if (field === 'category') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyPress}
                autoFocus
              >
                {CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton size="small" onClick={saveEditing} color="primary" sx={{ minHeight: 44, minWidth: 44 }}>
              <SaveIcon />
            </IconButton>
            <IconButton size="small" onClick={cancelEditing} color="secondary" sx={{ minHeight: 44, minWidth: 44 }}>
              <CancelIcon />
            </IconButton>
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            type={field === 'quantity' || field === 'sellingPrice' || field === 'originalCost' ? 'number' : 'text'}
            inputProps={{ 
              min: 0, 
              step: field === 'quantity' ? 1 : 0.01 
            }}
            autoFocus
            sx={{ width: 150 }}
          />
          <IconButton size="small" onClick={saveEditing} color="primary">
            <SaveIcon />
          </IconButton>
          <IconButton size="small" onClick={cancelEditing} color="secondary">
            <CancelIcon />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box
        onClick={() => startEditing(rowIndex, field, value)}
        sx={{
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(229,57,53,0.06)' },
          padding: '8px',
          borderRadius: '4px',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {field === 'category' ? (
          <Chip label={value || 'Tire'} size="small" color="primary" />
        ) : field === 'sellingPrice' || field === 'originalCost' ? (
          `₱${(value || 0).toFixed(2)}`
        ) : (
          value || ''
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={window.innerWidth < 600}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Low Stock Items</Typography>
          <Chip 
            label={`${lowStockItems.length} items`} 
            color="warning" 
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click any cell to edit. Press Enter to save, Escape to cancel.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading low stock items...</Typography>
          </Box>
        ) : lowStockItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No low stock items found. All items have sufficient inventory.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: { xs: '70vh', sm: '60vh' } }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', px: { xs: 1, sm: 2 } }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', px: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', px: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', px: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', px: { xs: 1, sm: 2 } }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', px: { xs: 1, sm: 2 } }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', px: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lowStockItems.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ px: { xs: 1, sm: 2 } }}>{renderCell(item, 'name', item.name, index)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>{renderCell(item, 'brand', item.brand, index)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>{renderCell(item, 'category', item.category, index)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>{renderCell(item, 'description', item.description, index)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderCell(item, 'quantity', item.quantity, index)}
                        <Chip label="Low" color="warning" size="small" sx={{ fontSize: '0.65rem' }} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 } }}>{renderCell(item, 'sellingPrice', item.sellingPrice, index)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>{renderCell(item, 'originalCost', item.originalCost, index)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ minHeight: 44 }}>Close</Button>
        <Button onClick={loadLowStockItems} variant="outlined" sx={{ minHeight: 44 }}>
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LowStockDialog;

